import React from "react";
import cn from "classnames";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import closeGrayIcon from "@src/assets/svg/close-icon-gray.svg";
import thingsTwinsIcon from "@src/assets/svg/things-twins-blue.svg";
import lockClosedIcon from "@src/assets/svg/lock-closed-gray.svg";
import closedPasswordEye from "@src/assets/svg/password-eye-closed-icon.svg";
import openPasswordEye from "@src/assets/svg/password-eye-open-icon.svg";
import radioButtonActive from "@src/assets/svg/radio-button-active.svg";
import radioButtonInactive from "@src/assets/svg/radio-button-inactive.svg";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import {
  DeviceSecurityMode,
  deviceSecurityModes,
} from "@src/types/device-security-mode";
import {
  DeviceSecurityPolicy,
  deviceSecurityPolicies,
} from "@src/types/device-security-policy";
import { AutoGenerateCertificateSetting } from "@src/components/atoms/auto-generate-certificate-setting/auto-generate-certificate-setting";
import { DropAreaAddSourceGateway } from "@src/components/atoms/drop-area-add-source-gateway/drop-area-add-source-gateway";
import { HelpIconPopover } from "@src/components/atoms/help-icon-popover/help-icon-popover";
import { useAddNewMachineDrawerModalContext } from "./add-new-machine-drawer-modal-context";
import styles from "./add-new-machine-drawer-modal.module.scss";

const cssClassName = "add-new-machine-drawer-modal";

const authOptions = [
  DeviceAuthOption.ANONYMOUS,
  DeviceAuthOption.USER_LOGIN,
] as const;

export const TwinSkillCard = () => {
  const {
    texts,
    machineSkillsTexts,
    twinTypeTabs,
    isTwinExpanded,
    isTwinInstalled,
    twinPort,
    rpcDsn,
    selectedTwinType,
    opcUaSettings,
    isAutoGenerateEnabled,
    showReuseGeneratedCertificateHint,
    displayUserLoginPasswordAsText,
    displayClientCertificatePasswordAsText,
    isInstallSkillsLoading,
    showTwinPortAvailable,
    setIsTwinExpanded,
    setTwinPort,
    setRpcDsn,
    setSelectedTwinType,
    setOpcUaSettings,
    toggleAutoGenerateCertificate,
    handlePasswordButton,
    handleClientCertificatePasswordButton,
  } = useAddNewMachineDrawerModalContext();

  const authOptionLabels = {
    [DeviceAuthOption.ANONYMOUS]: machineSkillsTexts.anonymousTab,
    [DeviceAuthOption.USER_LOGIN]: machineSkillsTexts.userLoginTab,
  };

  return (
    <div
      className={cn(styles["skill-card"], {
        [styles["skill-card-installed"]]: isTwinInstalled,
      })}
      title={
        isTwinInstalled
          ? machineSkillsTexts.installedSkillTooltip.replace(
              "{skill}",
              selectedTwinType || "OPCUA",
            )
          : undefined
      }
      aria-disabled={isTwinInstalled}
    >
      <div className={styles["skill-card-header"]}>
        <div className={styles["skill-card-header-left"]}>
          <img
            src={thingsTwinsIcon}
            alt={machineSkillsTexts.thingsTwinTitle}
            className={styles["skill-card-icon"]}
          />
          <h4 className={styles["skill-card-title"]}>
            {machineSkillsTexts.thingsTwinTitle}
          </h4>
          {isTwinInstalled && (
            <span
              className={styles["skill-installed-badge"]}
              title={machineSkillsTexts.installedSkillTooltip.replace(
                "{skill}",
                selectedTwinType || "OPCUA",
              )}
            >
              <img src={lockClosedIcon} alt="" />
              {machineSkillsTexts.installedSkillLabel}
            </span>
          )}
        </div>
        <button
          type="button"
          className={styles["toggle-skill-button"]}
          disabled={isTwinInstalled}
          onClick={() => setIsTwinExpanded(!isTwinExpanded)}
        >
          <span className={styles["toggle-skill-button-symbol"]}>
            {isTwinExpanded ? "−" : "+"}
          </span>
        </button>
      </div>
      <div className={styles["skill-card-content"]}>
        <p className={styles["skill-card-description"]}>
          {machineSkillsTexts.thingsTwinDescription}
        </p>
      </div>

      {isTwinExpanded && (
        <fieldset
          className={cn(
            styles["skill-card-expanded-content"],
            styles["skill-card-fieldset"],
          )}
          disabled={isTwinInstalled}
        >
          <div className={styles["security-sections"]}>
            <section className={styles["security-section"]}>
              <div className={styles["security-section-heading-row"]}>
                <h3 className={styles["security-section-title"]}>
                  <span>1.</span> {machineSkillsTexts.connectionSectionTitle}
                </h3>
                <HelpIconPopover
                  helpLabel={texts.help}
                  helpText={
                    selectedTwinType === "RPC"
                      ? machineSkillsTexts.rpcPortHelpText
                      : machineSkillsTexts.opcUaPortHelpText
                  }
                />
              </div>

              <div className={styles["security-section-content"]}>
                <div className={styles["skill-type-options"]}>
                  {twinTypeTabs.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`${styles["skill-type-tab"]} ${
                        selectedTwinType === type
                          ? styles["skill-type-tab-active"]
                          : ""
                      }`}
                      disabled={isTwinInstalled || type === "RPC"}
                      onClick={() => setSelectedTwinType(type)}
                    >
                      {type === "OPCUA" ? "OPC UA" : "RPC"}
                    </button>
                  ))}
                </div>

                {selectedTwinType === "OPCUA" && (
                  <>
                    <p className={styles["security-section-description"]}>
                      {machineSkillsTexts.connectionOpcUaDescription}
                    </p>
                    <div className={styles[`${cssClassName}__input-container`]}>
                      <div className={styles[`${cssClassName}__input-wrapper`]}>
                        <input
                          type="text"
                          aria-label={
                            machineSkillsTexts.connectionOpcUaDescription
                          }
                          placeholder={machineSkillsTexts.opcUaPortPlaceholder}
                          value={twinPort}
                          className={styles[`${cssClassName}__input`]}
                          onChange={(e) => setTwinPort(e.target.value)}
                        />
                        {twinPort && !isTwinInstalled && (
                          <button
                            type="button"
                            className={styles["port-clear-button"]}
                            aria-label={machineSkillsTexts.clearPortButtonLabel}
                            onClick={() => setTwinPort("")}
                          >
                            <img src={closeGrayIcon} alt="" />
                          </button>
                        )}
                      </div>
                      {showTwinPortAvailable && (
                        <p className={styles["port-available-text"]}>
                          {machineSkillsTexts.portIsAvailableLabel}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {selectedTwinType === "RPC" && (
                  <div className={styles[`${cssClassName}__input-container`]}>
                    <label
                      className={styles[`${cssClassName}__label`]}
                      htmlFor="rpc-dsn"
                    >
                      {machineSkillsTexts.rpcDsnLabel}
                    </label>
                    <div className={styles[`${cssClassName}__input-wrapper`]}>
                      <span className={styles[`${cssClassName}__dsn-prefix`]}>
                        rpc://
                      </span>
                      <input
                        id="rpc-dsn"
                        type="text"
                        value={rpcDsn}
                        className={styles[`${cssClassName}__input`]}
                        onChange={(e) => setRpcDsn(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            {selectedTwinType === "OPCUA" && (
              <>
                <section className={styles["security-section"]}>
                  <div className={styles["security-section-heading-row"]}>
                    <h3 className={styles["security-section-title"]}>
                      <span>2.</span>{" "}
                      {machineSkillsTexts.encryptionSectionTitle}
                    </h3>
                  </div>
                  <div className={styles["security-section-content"]}>
                    <p className={styles["security-section-description"]}>
                      {machineSkillsTexts.encryptionDescription}
                    </p>
                    <div className={styles["security-fields-row"]}>
                      <label
                        className={styles[`${cssClassName}__dropdown-label`]}
                      >
                        <span
                          className={
                            styles[`${cssClassName}__dropdown-label-text`]
                          }
                        >
                          {machineSkillsTexts.securityModeLabel}
                        </span>
                        <select
                          value={opcUaSettings.securityMode}
                          onChange={(e) =>
                            setOpcUaSettings({
                              ...opcUaSettings,
                              securityMode: e.target
                                .value as DeviceSecurityMode,
                            })
                          }
                          className={styles[`${cssClassName}__dropdown`]}
                        >
                          {deviceSecurityModes.map((mode) => (
                            <option key={mode} value={mode}>
                              {mode}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label
                        className={styles[`${cssClassName}__dropdown-label`]}
                      >
                        <span
                          className={
                            styles[`${cssClassName}__dropdown-label-text`]
                          }
                        >
                          {machineSkillsTexts.securityPolicyLabel}
                        </span>
                        <select
                          value={opcUaSettings.securityPolicy}
                          onChange={(e) =>
                            setOpcUaSettings({
                              ...opcUaSettings,
                              securityPolicy: e.target
                                .value as DeviceSecurityPolicy,
                            })
                          }
                          className={styles[`${cssClassName}__dropdown`]}
                        >
                          {deviceSecurityPolicies.map((policy) => (
                            <option key={policy} value={policy}>
                              {policy}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                </section>

                <section className={styles["security-section"]}>
                  <div className={styles["security-section-heading-row"]}>
                    <h3 className={styles["security-section-title"]}>
                      <span>3.</span> {machineSkillsTexts.loginSectionTitle}
                    </h3>
                  </div>
                  <div className={styles["security-section-content"]}>
                    <p className={styles["security-section-description"]}>
                      {machineSkillsTexts.loginDescription}
                    </p>
                    <div
                      className={styles["login-radio-group"]}
                      role="radiogroup"
                      aria-label={machineSkillsTexts.loginSectionTitle}
                    >
                      {authOptions.map((option) => (
                        <label
                          key={option}
                          className={styles["login-radio-option"]}
                        >
                          <input
                            type="radio"
                            name="opcua-auth-option"
                            value={option}
                            checked={opcUaSettings.authOption === option}
                            onChange={() =>
                              setOpcUaSettings({
                                ...opcUaSettings,
                                authOption: option,
                              })
                            }
                          />
                          <img
                            src={
                              opcUaSettings.authOption === option
                                ? radioButtonActive
                                : radioButtonInactive
                            }
                            alt=""
                          />
                          <span>{authOptionLabels[option]}</span>
                        </label>
                      ))}
                    </div>

                    {opcUaSettings.authOption ===
                      DeviceAuthOption.USER_LOGIN && (
                      <FlexColumnContainer verticalGap={16}>
                        <div
                          className={styles[`${cssClassName}__input-container`]}
                        >
                          <label
                            className={styles[`${cssClassName}__label`]}
                            htmlFor="opcua-username"
                          >
                            {machineSkillsTexts.usernameLabel}
                          </label>
                          <div
                            className={styles[`${cssClassName}__input-wrapper`]}
                          >
                            <input
                              id="opcua-username"
                              type="text"
                              value={opcUaSettings.username}
                              className={styles[`${cssClassName}__input`]}
                              onChange={(e) =>
                                setOpcUaSettings({
                                  ...opcUaSettings,
                                  username: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div
                          className={styles[`${cssClassName}__input-container`]}
                        >
                          <label
                            className={styles[`${cssClassName}__label`]}
                            htmlFor="opcua-password"
                          >
                            {machineSkillsTexts.passwordLabel}
                          </label>
                          <div
                            className={styles[`${cssClassName}__input-wrapper`]}
                          >
                            <input
                              id="opcua-password"
                              type={
                                displayUserLoginPasswordAsText
                                  ? "text"
                                  : "password"
                              }
                              value={opcUaSettings.password}
                              className={styles[`${cssClassName}__input`]}
                              onChange={(e) =>
                                setOpcUaSettings({
                                  ...opcUaSettings,
                                  password: e.target.value,
                                })
                              }
                            />
                            <IconButton
                              fun={(e) => handlePasswordButton(e)}
                              icon={
                                displayUserLoginPasswordAsText
                                  ? closedPasswordEye
                                  : openPasswordEye
                              }
                            />
                          </div>
                        </div>
                      </FlexColumnContainer>
                    )}
                  </div>
                </section>

                <section className={styles["security-section"]}>
                  <div className={styles["security-section-heading-row"]}>
                    <h3 className={styles["security-section-title"]}>
                      <span>4.</span>{" "}
                      {machineSkillsTexts.applicationCertificateSectionTitle}
                    </h3>
                  </div>
                  <div className={styles["security-section-content"]}>
                    <p className={styles["security-section-description"]}>
                      {machineSkillsTexts.applicationCertificateDescription}
                    </p>
                    <AutoGenerateCertificateSetting
                      isAutoGenerateEnabled={isAutoGenerateEnabled}
                      onToggle={toggleAutoGenerateCertificate}
                      disabled={isInstallSkillsLoading}
                      showReuseGeneratedCertificateHint={
                        showReuseGeneratedCertificateHint
                      }
                    />
                    {!isAutoGenerateEnabled && (
                      <>
                        <DropAreaAddSourceGateway<
                          typeof opcUaSettings,
                          "clientCertificateBase64"
                        >
                          label={machineSkillsTexts.clientCertificateLabel}
                          values={opcUaSettings}
                          keyProp="clientCertificateBase64"
                          setValues={setOpcUaSettings}
                        />
                        <DropAreaAddSourceGateway<
                          typeof opcUaSettings,
                          "clientCertificatePrivateKeyBase64"
                        >
                          label={
                            machineSkillsTexts.clientCertificatePrivateKeyLabel
                          }
                          values={opcUaSettings}
                          keyProp="clientCertificatePrivateKeyBase64"
                          setValues={setOpcUaSettings}
                        />
                        <div
                          className={styles[`${cssClassName}__input-container`]}
                        >
                          <label
                            className={styles[`${cssClassName}__label`]}
                            htmlFor="client-certificate-password"
                          >
                            {machineSkillsTexts.clientCertificatePasswordLabel}
                          </label>
                          <div
                            className={styles[`${cssClassName}__input-wrapper`]}
                          >
                            <input
                              id="client-certificate-password"
                              type={
                                displayClientCertificatePasswordAsText
                                  ? "text"
                                  : "password"
                              }
                              value={opcUaSettings.clientCertificatePassword}
                              className={styles[`${cssClassName}__input`]}
                              onChange={(e) =>
                                setOpcUaSettings({
                                  ...opcUaSettings,
                                  clientCertificatePassword: e.target.value,
                                })
                              }
                            />
                            <IconButton
                              fun={(e) =>
                                handleClientCertificatePasswordButton(e)
                              }
                              icon={
                                displayClientCertificatePasswordAsText
                                  ? closedPasswordEye
                                  : openPasswordEye
                              }
                            />
                          </div>
                        </div>
                        <DropAreaAddSourceGateway<
                          typeof opcUaSettings,
                          "serverPublicKeyBase64"
                        >
                          label={machineSkillsTexts.serverPublicKeyLabel}
                          values={opcUaSettings}
                          keyProp="serverPublicKeyBase64"
                          setValues={setOpcUaSettings}
                        />
                      </>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        </fieldset>
      )}
    </div>
  );
};
